import { Injectable, inject } from '@angular/core';
import { CookieService, type SameSite } from 'ngx-cookie-service';
import { BehaviorSubject, type Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private cookieService = inject(CookieService);
  private storageSubjects: Record<string, BehaviorSubject<unknown>> = {};

  constructor() {
    this.initStorageEventListener();
  }

  getCookieByName(name: string): string {
    return this.cookieService.get(name);
  }

  setCookie(
    name: string,
    value: string,
    expires?: number | Date,
    path = '/',
    domain?: string,
    secure = true,
    sameSite: SameSite = 'Strict'
  ) {
    this.cookieService.set(name, value, expires, path, domain, secure, sameSite);
  }

  removeCookie(name: string): void {
    this.cookieService.delete(name);
  }

  setLocalStorage<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
    this.emitChange(key, value);
  }

  getLocalStorage<T>(key: string): T | null {
    const item = localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  }

  removeLocalStorage(key: string): void {
    localStorage.removeItem(key);
    this.emitChange(key, null);
  }

  getDataObservable<T>(key: string): Observable<T | null> {
    if (!this.storageSubjects[key]) {
      this.storageSubjects[key] = new BehaviorSubject<T | null>(
        this.getLocalStorage<T>(key)
      ) as BehaviorSubject<unknown>;
    }
    return this.storageSubjects[key].asObservable() as Observable<T | null>;
  }

  private emitChange<T>(key: string, value: T | null): void {
    if (this.storageSubjects[key]) {
      this.storageSubjects[key].next(value);
    }
  }

  private initStorageEventListener() {
    window.addEventListener('storage', (event) => {
      if (event.key && this.storageSubjects[event.key]) {
        const newValue = event.newValue ? JSON.parse(event.newValue) : null;
        this.storageSubjects[event.key].next(newValue);
      }
    });
  }
}
